import React, { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import MagneticButton from "./MagneticButton";
import { AUDIT_LIST } from "./audits";

const LINKS = [
  { label: "About", to: "/#about" },
  { label: "Expertise", to: "/#services" },
  { label: "Credentials", to: "/#certifications" },
  { label: "Writing", to: "/blog" },
  { label: "Audits", group: true },
  { label: "Playbook", to: "/playbook" },
  { label: "Contact", to: "/#contact" },
];

const AuditsMenu = ({ pathname }) => {
  const [open, setOpen] = useState(false);
  const active = pathname === "/audits" || AUDIT_LIST.some((a) => a.path === pathname);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={(e) => (e.detail ? setOpen(true) : setOpen((v) => !v))}
        data-testid="nav-link-audits"
        className={`nav-link inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.22em] ${active ? "text-signal" : "text-ink"}`}
      >
        Audits <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4" data-testid="nav-audits-menu">
          <div className="w-64 bg-paper border border-ink shadow-[6px_6px_0_0_#0A0A0A]">
            <Link
              to="/audits"
              onClick={() => setOpen(false)}
              data-testid="nav-audit-compare"
              className={`flex items-center justify-between px-5 py-4 border-b border-ink font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors ${pathname === "/audits" ? "text-signal" : "text-ink"}`}
            >
              All audits — compare <span aria-hidden>→</span>
            </Link>
            {AUDIT_LIST.map((a) => (
              <Link
                key={a.key}
                to={a.path}
                onClick={() => setOpen(false)}
                data-testid={`nav-audit-${a.key}`}
                className={`flex items-center justify-between px-5 py-4 border-b border-line last:border-b-0 font-heading font-bold text-[14px] hover:bg-surface transition-colors ${pathname === a.path ? "text-signal" : "text-ink"}`}
              >
                {a.platform} Audit
                <span className="w-2 h-2" style={{ background: a.accent }} />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileAudits = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="mobile-audits-toggle"
        className="w-full flex items-center justify-between font-heading font-bold text-xl text-ink py-1"
      >
        Audits <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 ml-1 pl-4 border-l border-ink flex flex-col" data-testid="mobile-audits-menu">
          <Link to="/audits" onClick={onNavigate} data-testid="mobile-audit-compare" className="py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-signal">
            All audits — compare
          </Link>
          {AUDIT_LIST.map((a) => (
            <Link
              key={a.key}
              to={a.path}
              onClick={onNavigate}
              data-testid={`mobile-audit-${a.key}`}
              className="py-3 font-heading font-bold text-base text-ink"
            >
              {a.platform} Audit
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

function useHashScroll() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // slight delay to let target section mount
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 40);
    }
  }, [location]);
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useHashScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Pick home anchor hrefs when already on "/"
  const toFor = (t) => {
    if (t.startsWith("/#") && location.pathname === "/") {
      return { hash: t.slice(1) }; // keep hash only
    }
    return t;
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-paper/[0.97] backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <Logo variant="dark" size={36} className="group-hover:bg-signal transition-colors" />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-heading font-black text-[15px] tracking-tight">ABHINAV DHIMAN</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
              The Digital Marketer
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => {
            if (l.group) return <AuditsMenu key="audits" pathname={location.pathname} />;
            const active =
              (l.to === "/blog" && location.pathname === "/blog") ||
              (l.to === "/playbook" && location.pathname.startsWith("/playbook")) ||
              (l.to.startsWith("/#") && location.pathname === "/" && location.hash === l.to.slice(1));
            return (
              <Link
                key={l.to}
                to={toFor(l.to)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`nav-link font-mono text-[11px] uppercase tracking-[0.22em] ${
                  active ? "text-signal" : "text-ink"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <MagneticButton strength={0.3} className="hidden lg:inline-block">
          <Link
            to={location.pathname === "/" ? { hash: "#contact" } : "/#contact"}
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-2 bg-ink text-paper hover:bg-signal px-5 py-3 font-heading font-bold text-[12px] uppercase tracking-[0.18em] transition-colors"
          >
            Book a Call
            <span aria-hidden>→</span>
          </Link>
        </MagneticButton>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 border border-ink"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-paper border-t border-line">
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) => l.group ? (
              <MobileAudits key="audits" onNavigate={() => setOpen(false)} />
            ) : (
              <Link
                key={l.to}
                to={toFor(l.to)}
                onClick={() => setOpen(false)}
                className="font-heading font-bold text-xl text-ink"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to={location.pathname === "/" ? { hash: "#contact" } : "/#contact"}
              onClick={() => setOpen(false)}
              className="inline-flex w-fit bg-ink text-paper px-5 py-3 font-heading font-bold text-[12px] uppercase tracking-[0.18em]"
            >
              Book a Call →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
